import type { TAllowedColors } from '@components/atoms/tag/types';
import type { CellContext } from '@tanstack/react-table';
import { StatusCell, type StatusCellProps } from '../../components/status-cell';
import type { DataGridColumnDef, DataGridColumnDefOptions } from '../../data-grid.type';

/**
 * Creates a cell renderer for status cells with support for icons, tags, and CTAs
 *
 * @param col - The column definition
 * @param options - Additional options including parse function
 * @returns A cell renderer function that creates StatusCell components
 *
 * @example
 * // Basic usage with parsing function
 * {
 *   key: 'status',
 *   header: 'Status',
 *   type: 'status',
 *   parse: (value) => ({
 *     tags: [{ value: value.status, color: value.isActive ? 'green' : 'red' }],
 *     icons: [<AlertIcon />],
 *     moreContent: 'View details'
 *   })
 * }
 */
export function getColumnDefStatus<T>(col: DataGridColumnDef<T>, options: DataGridColumnDefOptions) {
	return function StatusCellColumn(context: CellContext<T, unknown>) {
		const value = context.getValue();
		const row = context.row.original;

		if (value === null || value === undefined) return null;

		const statusProps: StatusCellProps = {
			iconsLeft: [],
			iconsRight: [],
			tags: [],
		};

		if (options?.parse) {
			const parsed = options.parse(value, row, options);

			// Handle simple status similar to getColumnDefTags for backward compatibility
			if (parsed.value && parsed.color) {
				statusProps.tags = [
					{
						value: String(parsed.value),
						color: parsed.color as TAllowedColors,
					},
				];
			}

			// Handle icons with backward compatibility
			if (parsed.icons && !parsed.iconsLeft) {
				statusProps.iconsLeft = parsed.icons;
			} else if (parsed.iconsLeft) {
				statusProps.iconsLeft = parsed.iconsLeft;
			}

			// Handle new iconsRight property
			if (parsed.iconsRight) {
				statusProps.iconsRight = parsed.iconsRight;
			}

			if (parsed.tags) statusProps.tags = parsed.tags;
			if (parsed.ctaIcon) statusProps.ctaIcon = parsed.ctaIcon;
			if (parsed.ctaTooltip) statusProps.ctaTooltip = parsed.ctaTooltip;
			if (parsed.moreContent) statusProps.moreContent = parsed.moreContent;
			if (parsed.moreContentHover) statusProps.moreContentHover = parsed.moreContentHover;
			if (parsed.moreContentLink) statusProps.moreContentLink = parsed.moreContentLink;
			if (parsed.dataTestId) statusProps.dataTestId = parsed.dataTestId;
		}

		return <StatusCell {...statusProps} />;
	};
}
